const rangesClose = (a, b, epsilon = 0.01) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(Number(a.from) - Number(b.from)) <= epsilon &&
    Math.abs(Number(a.to) - Number(b.to)) <= epsilon
  );
};

const rebaseRangeForPointCount = (range, previousPointCount, nextPointCount) => {
  if (
    !range ||
    !Number.isFinite(Number(previousPointCount)) ||
    !Number.isFinite(Number(nextPointCount)) ||
    Number(previousPointCount) <= 0 ||
    Number(nextPointCount) <= 0
  ) {
    return range;
  }
  const span = range.to - range.from;
  const rightOffset = range.to - (Number(previousPointCount) - 1);
  const to = Number(nextPointCount) - 1 + rightOffset;
  return { from: to - span, to };
};

export const createSharedRangeBus = ({
  scheduleFrame = null,
  cancelFrame = null,
} = {}) => {
  let currentRange = null;
  let currentContext = null;
  let pendingNotification = null;
  let notificationFrame = null;
  const listeners = new Set();

  const requestFrame =
    typeof scheduleFrame === "function"
      ? scheduleFrame
      : typeof window !== "undefined" &&
          typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame.bind(window)
        : null;
  const cancelScheduledFrame =
    typeof cancelFrame === "function"
      ? cancelFrame
      : typeof window !== "undefined" &&
          typeof window.cancelAnimationFrame === "function"
        ? window.cancelAnimationFrame.bind(window)
        : null;

  const notify = (range, sourceId, context) => {
    listeners.forEach((listener) => {
      try {
        listener(range, sourceId, context);
      } catch (_) {}
    });
  };

  const flushPendingNotification = () => {
    const pending = pendingNotification;
    pendingNotification = null;
    if (!pending) return;
    notify(pending.range, pending.sourceId, pending.context);
  };

  const discardPendingNotification = () => {
    pendingNotification = null;
    if (notificationFrame != null && cancelScheduledFrame) {
      cancelScheduledFrame(notificationFrame);
    }
    notificationFrame = null;
  };

  const scheduleNotification = (range, sourceId, context) => {
    pendingNotification = { range, sourceId, context };
    if (!requestFrame) {
      flushPendingNotification();
      return;
    }
    if (notificationFrame != null) return;
    notificationFrame = requestFrame(() => {
      notificationFrame = null;
      flushPendingNotification();
    });
  };

  return {
    getRange() {
      return currentRange;
    },
    getRangeSnapshot() {
      if (!currentRange) return null;
      return {
        range: { ...currentRange },
        pointCount: currentContext?.pointCount ?? null,
        scale: currentContext?.scale ?? null,
      };
    },
    setRange(range, sourceId = null, context = null, options = null) {
      const normalized =
        range &&
        Number.isFinite(range.from) &&
        Number.isFinite(range.to)
          ? { from: Number(range.from), to: Number(range.to) }
          : null;
      const normalizedContext = normalized
        ? {
            pointCount:
              Number.isFinite(Number(context?.pointCount)) &&
              Number(context.pointCount) > 0
                ? Number(context.pointCount)
                : null,
            scale:
              context?.scale != null && String(context.scale).trim()
                ? String(context.scale)
                : null,
          }
        : null;

      const projectedCurrentRange =
        currentRange &&
        currentContext?.scale &&
        currentContext.scale === normalizedContext?.scale
          ? rebaseRangeForPointCount(
              currentRange,
              currentContext.pointCount,
              normalizedContext.pointCount,
            )
          : currentRange;
      if (rangesClose(projectedCurrentRange, normalized)) {
        return currentRange;
      }

      currentRange = normalized;
      currentContext = normalizedContext;
      if (options?.delivery === "frame") {
        scheduleNotification(currentRange, sourceId, currentContext);
      } else {
        discardPendingNotification();
        notify(currentRange, sourceId, currentContext);
      }
      return currentRange;
    },
    clear(sourceId = null) {
      if (currentRange == null) return null;
      discardPendingNotification();
      currentRange = null;
      currentContext = null;
      notify(null, sourceId, null);
      return null;
    },
    subscribe(listener) {
      if (typeof listener !== "function") {
        return () => {};
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};
